import {AiQueryCapabilityResult, AiQueryRecipe, fetchAiQueryDataset} from './adminAiQueryApi';
import {getProjectionForCapability} from './aiQueryColumns';
import {buildCsv, csvFilename, downloadCsv} from './aiQueryCsv';

/**
 * Where a turn's full dataset rows live. The Ask drawer's stream sends each
 * dataset as a preview; a download fetches the rest by turn id + handle.
 */
export type AiQueryDatasetSource = {
  turnId: string;
  recipe: AiQueryRecipe | null;
  elevated?: boolean;
};

/** Every row of the dataset, not just the preview the stream carried. */
export const datasetRowCount = (result: AiQueryCapabilityResult): number =>
  result.row_count ?? result.items.length;

/** Whether the stream sent only some of the dataset's rows. */
export const isDatasetPreview = (result: AiQueryCapabilityResult): boolean =>
  datasetRowCount(result) > result.items.length;

/** Whether a CSV of the dataset would hold anything: rows, and columns to show. */
export const isDownloadableDataset = (result: AiQueryCapabilityResult): boolean =>
  result.items.length > 0 &&
  getProjectionForCapability(result.capability, result.items).columns.length > 0;

/** The dataset with all its rows: as is when complete, fetched when a preview. */
export const fullDataset = async (
  result: AiQueryCapabilityResult,
  source?: AiQueryDatasetSource
): Promise<AiQueryCapabilityResult> => {
  if (!isDatasetPreview(result) || !source || !result.handle) {
    return result;
  }
  return fetchAiQueryDataset({
    turnId: source.turnId,
    handle: result.handle,
    recipe: source.recipe,
    elevated: source.elevated,
  });
};

/**
 * Download a dataset as CSV with all its rows. Columns come from the full
 * rows, so a column blank across the preview still exports.
 */
export const downloadDatasetCsv = async (
  result: AiQueryCapabilityResult,
  source?: AiQueryDatasetSource
): Promise<void> => {
  const full = await fullDataset(result, source);
  const projection = getProjectionForCapability(full.capability, full.items);
  downloadCsv(buildCsv(full.items, projection), csvFilename(projection));
};
